import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, from, mergeMap, of, switchMap, throwError } from 'rxjs';

import { API_BASE_URL } from '../constants/api-endpoints';
import { EncryptionSessionService } from '../services/encryption-session.service';

const EXEMPT_URL = /\/Security\/EncDecrypt|\/Master\/GetEnvironmentConfigs/i;
const RETRYABLE_CODES = new Set(['EncryptionRequired', 'EncryptionSessionInvalid']);
const SECURE_SESSION_ERROR = 'A secure connection could not be established. Please refresh the page.';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  private readonly session = inject(EncryptionSessionService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.run(request, next, true);
  }

  private run(request: HttpRequest<unknown>, next: HttpHandler, allowRetry: boolean): Observable<HttpEvent<unknown>> {
    if (!this.applies(request)) {
      return next.handle(request);
    }

    return from(this.ensureReady()).pipe(
      switchMap((ready) => {
        if (!ready) {
          return this.session.isEnabled
            ? throwError(() => new Error(SECURE_SESSION_ERROR))
            : next.handle(request);
        }

        const associatedBase = this.associatedBase(request);

        return from(this.encryptRequest(request, associatedBase)).pipe(
          switchMap((encrypted) => next.handle(encrypted)),
          mergeMap((event) => (event instanceof HttpResponse ? from(this.decryptResponse(event, associatedBase)) : of(event))),
          catchError((error: unknown) => this.handleError(error, request, next, associatedBase, allowRetry))
        );
      })
    );
  }

  private applies(request: HttpRequest<unknown>): boolean {
    if (!request.url.startsWith(API_BASE_URL) || EXEMPT_URL.test(request.url)) {
      return false;
    }

    if (request.responseType === 'blob' || request.responseType === 'arraybuffer') {
      return false;
    }

    const body = request.body;
    return !(body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer);
  }

  private async ensureReady(): Promise<boolean> {
    if (this.session.isEnabled && !this.session.isReady) {
      await this.session.refresh();
    }
    return this.session.isReady;
  }

  private associatedBase(request: HttpRequest<unknown>): string {
    const path = decodeURIComponent(new URL(request.url, window.location.origin).pathname);
    return `${request.method}|${path}`;
  }

  private async encryptRequest(request: HttpRequest<unknown>, associatedBase: string): Promise<HttpRequest<unknown>> {
    const url = new URL(request.url, window.location.origin);
    const plainQuery = [url.search.replace(/^\?/, ''), request.params.toString()].filter(Boolean).join('&');

    let params = new HttpParams();
    if (plainQuery) {
      const envelope = await this.session.encrypt(plainQuery, associatedBase);
      params = params.set('q', this.toBase64Url(JSON.stringify(envelope)));
    }

    let body = request.body;
    if (body !== null && body !== undefined) {
      const plainBody = typeof body === 'string' ? body : JSON.stringify(body);
      body = await this.session.encrypt(plainBody, associatedBase);
    }

    return request.clone({
      url: request.url.split('?')[0],
      params,
      body,
      setHeaders: { 'X-Encrypted': '1', 'X-Enc-Ticket': this.session.currentTicket ?? '' }
    });
  }

  private async decryptResponse(response: HttpResponse<unknown>, associatedBase: string): Promise<HttpResponse<unknown>> {
    if (!this.session.isEnvelope(response.body)) {
      return response;
    }

    const plain = await this.session.decrypt(response.body, associatedBase);
    return response.clone({ body: this.parse(plain) });
  }

  private handleError(
    error: unknown,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    associatedBase: string,
    allowRetry: boolean
  ): Observable<never> | Observable<HttpEvent<unknown>> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    if (allowRetry && error.status === 428 && RETRYABLE_CODES.has(error.error?.errorDetails)) {
      return from(this.session.refresh()).pipe(switchMap(() => this.run(request, next, false)));
    }

    if (!this.session.isEnvelope(error.error)) {
      return throwError(() => error);
    }

    return from(this.session.decrypt(error.error, associatedBase)).pipe(
      switchMap((plain) =>
        throwError(
          () =>
            new HttpErrorResponse({
              error: this.parse(plain),
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url ?? undefined
            })
        )
      )
    );
  }

  private parse(text: string): unknown {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  private toBase64Url(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCodePoint(b)));
    return btoa(binary).split('=')[0].replaceAll('+', '-').replaceAll('/', '_');
  }
}
